import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { Btn, Card, Field, Input, Select, Textarea, Notice, Tag, ErrorSummary, cx } from "../components/kit";
import FileUploader from "../components/FileUploader";
import { api, newClientId, parseError } from "../lib/api";
import { useAuth } from "../lib/auth";

export const ACTION_FIELDS = ["objective", "procedure", "body_area", "products_tools", "preparation", "responsible_person", "progression_conditions", "stopping_grounds", "alternative", "observation_method"];
const uid = () => newClientId().replace(/-/g, "");

export function useMaterialLang() {
  const { me } = useAuth();
  return me?.user?.material_language || "en";
}

export function OriginLabel({ a }) {
  const { t } = useTranslation();
  const ml = useMaterialLang();
  if (a.origin === "user") return <Tag data-testid="origin-user" className="bg-[hsl(var(--sage))] text-[hsl(140_35%_22%)]">{t("origin.user")}</Tag>;
  const title = a.protocol_title?.[ml];
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" data-testid={`origin-${a.origin}`}>
      <Tag className="bg-[hsl(var(--lavender))] text-[hsl(268_32%_30%)]">{a.origin === "adaptation" ? t("origin.adaptation") : t("origin.element_demo")}</Tag>
      <span className="text-xs text-muted-foreground" data-testid="origin-version">{a.protocol_number} · {t("common.version_n", { n: a.protocol_version })} · {a.protocol_author}</span>
      {title ? <span className="text-xs">{title}</span> : <span className="text-xs italic text-muted-foreground">{t("library.no_edition", { lang: t(`lang.${ml}`) })}</span>}
    </span>
  );
}

function ActionEditor({ a, onChange, onRemove, products, errs, path }) {
  const { t } = useTranslation();
  const setField = (k, patch) => onChange({ ...a, fields: { ...a.fields, [k]: { value: "", na: false, na_reason: "", ...(a.fields?.[k] || {}), ...patch } } });
  const err = (k) => errs.find((e) => e.field === `${path}.${k}`);
  return (
    <div className="rounded-2xl border bg-white p-4" data-testid={`action-editor-${a.id}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input aria-label={t("field.action_title")} data-testid="action-title-input" className="flex-1" value={a.title} placeholder={t("field.action_title")} onChange={(e) => onChange({ ...a, title: e.target.value })} />
        <button onClick={onRemove} aria-label={t("common.remove")} className="inline-flex h-11 w-11 items-center justify-center rounded-xl hover:bg-muted"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="mb-3"><OriginLabel a={a} /></div>
      {a.origin === "adaptation" && (
        <Field label={t("field.adaptation_reason")} htmlFor={`ar-${a.id}`} required error={err("adaptation_reason") && t("err.required")}>
          <Input id={`ar-${a.id}`} data-testid="adaptation-reason-input" value={a.adaptation_reason || ""} onChange={(e) => onChange({ ...a, adaptation_reason: e.target.value })} />
        </Field>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        {ACTION_FIELDS.map((k) => {
          const v = a.fields?.[k] || {};
          const e = err(k);
          return (
            <div key={k} className={cx("rounded-xl border p-2.5", e && "border-destructive")}>
              <div className="flex items-center justify-between gap-2">
                <label htmlFor={`af-${a.id}-${k}`} className="text-sm font-semibold">{t(`afield.${k}`)}</label>
                <label className="flex min-h-[36px] items-center gap-1.5 text-xs font-semibold"><input type="checkbox" className="h-4 w-4" data-testid={`na-checkbox-${k}`} checked={!!v.na} onChange={(ev) => setField(k, { na: ev.target.checked })} />{t("common.na")}</label>
              </div>
              {v.na ? (
                <>
                  <Input id={`af-${a.id}-${k}`} data-testid={`na-reason-${k}`} className="mt-1" placeholder={t("programme.na_reason")} value={v.na_reason || ""} onChange={(ev) => setField(k, { na_reason: ev.target.value })} />
                  {e && <p role="alert" className="mt-1 text-xs text-destructive">{t("err.na_reason_required")}</p>}
                </>
              ) : <Textarea id={`af-${a.id}-${k}`} data-testid={`action-field-${k}`} rows={2} className="mt-1 min-h-[60px]" value={v.value || ""} onChange={(ev) => setField(k, { value: ev.target.value })} />}
            </div>
          );
        })}
      </div>
      <ProductPicker products={products} value={a.product_ids || []} onChange={(ids) => onChange({ ...a, product_ids: ids })} testid="action-products" />
    </div>
  );
}

export function ProductPicker({ products, value, onChange, testid }) {
  const { t } = useTranslation();
  return (
    <fieldset className="mt-3" data-testid={testid}>
      <legend className="mb-1 text-sm font-semibold">{t("programme.products")}</legend>
      <div className="flex flex-wrap gap-2">
        {products.map((p) => (
          <label key={p.id} className={cx("flex min-h-[40px] items-center gap-2 rounded-full border px-3 text-sm", value.includes(p.id) && "border-primary bg-[hsl(var(--peach))]")}>
            <input type="checkbox" className="h-4 w-4" checked={value.includes(p.id)} onChange={() => onChange(value.includes(p.id) ? value.filter((x) => x !== p.id) : [...value, p.id])} />
            {p.name} {p.user_entry ? <span className="text-xs font-semibold text-[hsl(32_80%_26%)]">({t("product.user_entry")})</span> : p.is_demo ? <span className="text-xs text-muted-foreground">({t("demo.short")})</span> : null}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function HomeActivityEditor({ h, onChange, onRemove, products, protocols }) {
  const { t } = useTranslation();
  const set = (k) => (e) => onChange({ ...h, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="rounded-2xl border border-[hsl(140_24%_80%)] bg-[hsl(140_30%_97%)] p-4" data-testid={`home-activity-editor-${h.id}`}>
      <div className="mb-3 flex gap-2">
        <Input aria-label={t("field.home_title")} data-testid="home-title-input" className="flex-1" value={h.title} placeholder={t("field.home_title")} onChange={set("title")} />
        <button onClick={onRemove} aria-label={t("common.remove")} className="inline-flex h-11 w-11 items-center justify-center rounded-xl hover:bg-muted"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label={t("home_field.standard_block")} htmlFor={`hb-${h.id}`} hint={t("home_field.standard_hint")}>
          <Select id={`hb-${h.id}`} data-testid="home-standard-block-select" value={h.protocol_id || ""} onChange={(e) => onChange({ ...h, protocol_id: e.target.value || null, protocol_version: null })}>
            <option value="">{t("home_field.free_text")}</option>
            {protocols.map((p) => <option key={p.id} value={p.id}>{p.number} · v{p.current_version}</option>)}
          </Select>
        </Field>
        {!h.protocol_id && (
          <Field label={t("home_field.text_language")} htmlFor={`hl-${h.id}`}>
            <Select id={`hl-${h.id}`} data-testid="home-text-language-select" value={h.text_language || "en"} onChange={set("text_language")}><option value="en">English</option><option value="ru">Русский</option></Select>
          </Field>
        )}
        {!h.protocol_id && <div className="md:col-span-2"><Field label={t("home_field.text")} htmlFor={`ht-${h.id}`}><Textarea id={`ht-${h.id}`} data-testid="home-text-input" value={h.text || ""} onChange={set("text")} /></Field></div>}
        {["explanation", "agreed_feasibility", "duration", "frequency", "materials", "stopping_conditions"].map((k) => (
          <Field key={k} label={t(`home_field.${k}`)} htmlFor={`h-${h.id}-${k}`}><Input id={`h-${h.id}-${k}`} data-testid={`home-${k}-input`} value={h[k] || ""} onChange={set(k)} /></Field>
        ))}
      </div>
      <ProductPicker products={products} value={h.permitted_product_ids || []} onChange={(ids) => onChange({ ...h, permitted_product_ids: ids })} testid="home-products" />
      <label className="mt-3 flex min-h-[44px] items-center gap-2 text-sm font-semibold"><input type="checkbox" className="h-5 w-5" data-testid="home-confirmed-checkbox" checked={!!h.groomer_confirmed} onChange={set("groomer_confirmed")} />{t("home_field.confirmed")}</label>
    </div>
  );
}

function UserProductForm({ pid, dogId, onAdded }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ name: "", function: "", source: "", photo_file_id: null });
  const [cid, setCid] = useState(newClientId());
  const [apiErr, setApiErr] = useState(null);
  const save = async () => {
    try {
      const { data } = await api.post(`/programmes/${pid}/user-products`, { ...f, client_id: cid });
      onAdded(data);
      setF({ name: "", function: "", source: "", photo_file_id: null });
      setCid(newClientId());
      setApiErr(null);
    } catch (e) { setApiErr(parseError(e)); }
  };
  return (
    <details className="rounded-2xl border bg-white p-4" data-testid="user-product-form">
      <summary className="cursor-pointer font-semibold">{t("product.add_unlisted")}</summary>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <ErrorSummary errors={apiErr?.errors} />
        <Field label={t("product.name")} htmlFor="up-n" required><Input id="up-n" data-testid="user-product-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label={t("product.function")} htmlFor="up-f"><Input id="up-f" data-testid="user-product-function-input" value={f.function} onChange={(e) => setF({ ...f, function: e.target.value })} /></Field>
        <Field label={t("product.source")} htmlFor="up-s" required><Input id="up-s" data-testid="user-product-source-input" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} /></Field>
      </div>
      <div className="mt-3"><FileUploader dogId={dogId} compact testid="user-product-photo" meta={{ title: f.name || "Product photo" }} onAdded={(file) => setF((s) => ({ ...s, photo_file_id: file.id }))} /></div>
      <p className="mt-2 text-xs text-muted-foreground">{t("product.user_entry_note")}</p>
      <Btn className="mt-3" variant="soft" testid="user-product-save-btn" onClick={save}>{t("product.save")}</Btn>
    </details>
  );
}

export default function ProgrammeEditor({ p, setP, products, setProducts, errs, dogId }) {
  const { t } = useTranslation();
  const [protocols, setProtocols] = useState([]);
  useEffect(() => { api.get("/protocols").then((r) => setProtocols(r.data)); }, []);
  const setStage = (i, s) => setP({ ...p, stages: p.stages.map((x, j) => (j === i ? s : x)) });
  const addAction = (i, dir, protoId) => {
    const pr = protocols.find((x) => x.id === protoId);
    const s = p.stages[i];
    const origin = protoId === "user" ? "user" : protoId?.startsWith("adapt:") ? "adaptation" : "element";
    const realId = protoId?.replace("adapt:", "");
    const pr2 = protocols.find((x) => x.id === realId) || pr;
    const a = { id: uid(), title: pr2 ? (pr2.edition?.title || pr2.number) : "", direction: dir, origin, protocol_id: origin === "user" ? null : realId,
      protocol_version: pr2?.current_version, protocol_number: pr2?.number, protocol_author: pr2?.author, protocol_title: pr2 ? { [pr2.edition ? "en" : "x"]: pr2.edition?.title } : null,
      fields: {}, product_ids: [] };
    setStage(i, { ...s, actions: [...s.actions, a] });
  };
  return (
    <div className="space-y-5">
      {p.stages.map((s, i) => (
        <Card key={s.id} data-testid={`stage-${i}`}>
          <div className="mb-4 flex items-center gap-2">
            <Tag>{t("programme.stage_n", { n: i + 1 })}</Tag>
            <Input aria-label={t("programme.stage_title")} data-testid="stage-title-input" value={s.title} placeholder={t("programme.stage_title")} onChange={(e) => setStage(i, { ...s, title: e.target.value })} />
          </div>
          {p.directions.map((dir) => (
            <section key={dir} className="mb-5" data-testid={`direction-section-${dir}`}>
              <h3 className="mb-2 font-display text-base font-bold">{dir} — {t(`dir.${dir}`)}</h3>
              <div className="space-y-3">
                {s.actions.map((a, j) => a.direction === dir && (
                  <ActionEditor key={a.id} a={a} products={products} errs={errs} path={`stages.${i}.actions.${j}`}
                    onChange={(na) => setStage(i, { ...s, actions: s.actions.map((x, k) => (k === j ? na : x)) })}
                    onRemove={() => setStage(i, { ...s, actions: s.actions.filter((_, k) => k !== j) })} />
                ))}
              </div>
              <Select className="mt-2" data-testid={`add-action-select-${dir}`} value="" aria-label={t("programme.add_action")} onChange={(e) => e.target.value && addAction(i, dir, e.target.value)}>
                <option value="">{t("programme.add_action")}…</option>
                <option value="user">{t("programme.add_user_action")}</option>
                <optgroup label={t("programme.from_demo")}>
                  {protocols.filter((x) => x.directions.includes(dir)).map((x) => <option key={x.id} value={x.id}>{x.number} · v{x.current_version} ({t("demo.short")})</option>)}
                </optgroup>
                <optgroup label={t("programme.adapt_demo")}>
                  {protocols.filter((x) => x.directions.includes(dir)).map((x) => <option key={x.id} value={`adapt:${x.id}`}>{t("origin.adaptation")}: {x.number}</option>)}
                </optgroup>
              </Select>
            </section>
          ))}
          <h3 className="mb-2 mt-4 font-display text-base font-bold">{t("programme.home_activities")}</h3>
          <div className="space-y-3">
            {s.home_activities.map((h, j) => (
              <HomeActivityEditor key={h.id} h={h} products={products} protocols={protocols}
                onChange={(nh) => setStage(i, { ...s, home_activities: s.home_activities.map((x, k) => (k === j ? nh : x)) })}
                onRemove={() => setStage(i, { ...s, home_activities: s.home_activities.filter((_, k) => k !== j) })} />
            ))}
          </div>
          <Btn variant="sage" className="mt-3" testid="add-home-activity-btn" onClick={() => setStage(i, { ...s, home_activities: [...s.home_activities, { id: uid(), title: "", text: "", text_language: "en", permitted_product_ids: [], groomer_confirmed: false }] })}>
            <Plus className="h-4 w-4" />{t("programme.add_home")}
          </Btn>
        </Card>
      ))}
      <Btn variant="outline" testid="add-stage-btn" onClick={() => setP({ ...p, stages: [...p.stages, { id: uid(), title: "", actions: [], home_activities: [] }] })}><Plus className="h-4 w-4" />{t("programme.add_stage")}</Btn>
      <UserProductForm pid={p.id} dogId={dogId} onAdded={(x) => setProducts([...products, x])} />
      {p.directions.length === 0 && <Notice tone="warn">{t("programme.no_directions")}</Notice>}
    </div>
  );
}
